import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { SeveridadeToast } from '../../models/enums/severidade-toast.enum';
import { Router } from '@angular/router';
import { Usuario } from '../../models/usuario.model';
import { FormatadorHelper } from '../../helpers/formatador.helper';
import { EmpresaDetalhes } from '../../models/empresaDetalhes.model';
import { JwtHelper } from '../../helpers/jwtDecode.helper';
import { AuthService } from '../../services/auth.service';
import { Icones } from '../../shared/variaveisDeRotas';
import { LocationService } from '../../services/location-service.service';
import { RegimeApuracaoSimplesNacional } from '../../models/enums/regimeApuracaoSimplesNacional.enum';
import { provideNgxMask } from 'ngx-mask';
import {
  CERTIFICADO_ACCEPT,
  CERTIFICADO_TAMANHO_MAX_BYTES,
  validarArquivoCertificado
} from '../../helpers/certificado-digital.helper';

interface Estado {
  label: string;
  value: string;
  id: string;
}

@Component({
  selector: 'app-empresa',
  templateUrl: './empresa.component.html',
  styleUrl: './empresa.component.scss',
  providers: [ConfirmationService, provideNgxMask()]
})
export class EmpresaComponent implements OnInit {

  @ViewChild('dt') dt: Table | undefined;
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  empresa: EmpresaDetalhes | null = null;
  private userId: string;
  certificadoBaseList: File[] = [];
  fileName: string | null = null;
  senhaCertificado: string = '';
  certificadoInvalid: boolean = false;
  isSubmitting: boolean = false;
  exibirAjudaCertificado: boolean = false;
  readonly acceptCertificado = CERTIFICADO_ACCEPT;
  readonly maxCertificadoBytes = CERTIFICADO_TAMANHO_MAX_BYTES;
  passosAjudaCertificado: string[] = [
    'Acesse "Configurações" > "Dados da Empresa"',
    'Role a página até a seção "Certificado Digital"',
    'Clique em "Selecionar Certificado" e escolha o arquivo .pfx ou .p12 (máx. 5MB)',
    'Digite a senha definida na emissão do certificado',
    'Clique em "Atualizar Certificado" para concluir'
  ];
  alertaRequisitosCertificado: string = 'O certificado só será aceito se estiver válido, for do tipo A1 (.pfx ou .p12) e estiver vinculado ao CNPJ desta empresa.';
  isEditMode: boolean = false;
  dadosForm: FormGroup;
  estados: Estado[] = [];
  municipios: { label: string; value: string }[] = [];
  regimeApuracaoOptions: { label: string; value: number }[] = [];
  isSubmittingDados: boolean = false;

  constructor(
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private router: Router,
    private jwtHelper: JwtHelper,
    private authService: AuthService,
    private locationService: LocationService,
    private fb: FormBuilder
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
    this.initializeForm();
    this.initializeRegimeApuracaoOptions();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Detalhes da Empresa');
    this.iconeChange.emit(Icones.listaAmarelo);
    this.carregarDetalhesEmpresa();
    this.inicializarDadosIBGE();
  }

  initializeForm(): void {
    this.dadosForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      telefone: ['', [Validators.required, Validators.maxLength(20)]],
      regimeApuracaoSimplesNacional: [RegimeApuracaoSimplesNacional.Competencia],
      logradouro: ['', [Validators.required, Validators.maxLength(125)]],
      numero: ['', [Validators.required, Validators.maxLength(10)]],
      complemento: ['', [Validators.required, Validators.maxLength(60)]],
      bairro: ['', [Validators.required, Validators.maxLength(60)]],
      uf: ['', [Validators.required, Validators.maxLength(2)]],
      municipio: ['', [Validators.required]],
      cep: ['', [Validators.required, Validators.maxLength(15)]],
      codigoMunicipio: [{ value: '', disabled: true }, [Validators.required]],
      codigoTributarioMunicipio: ['', [Validators.required]],
      pais: ['BR', [Validators.required, Validators.maxLength(100)]]
    });
  }

  initializeRegimeApuracaoOptions(): void {
    this.regimeApuracaoOptions = [
      { label: 'Caixa', value: RegimeApuracaoSimplesNacional.Caixa },
      { label: 'Competência', value: RegimeApuracaoSimplesNacional.Competencia }
    ];
  }

  inicializarDadosIBGE(): void {
    this.spinnerService.show('Carregando estados...');
    this.locationService.getEstados().subscribe(estados => {
      this.estados = estados
        .map((estado: any) => ({
          label: estado.nome,
          value: estado.sigla,
          id: estado.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      this.spinnerService.hide();
    });

    this.dadosForm.get('uf')?.valueChanges.subscribe(ufSigla => {
      if (ufSigla) {
        const estado = this.estados.find(e => e.value === ufSigla);
        if (estado) {
          this.spinnerService.show('Carregando municípios...');
          this.locationService.getMunicipiosPorUf(estado.id).subscribe(municipios => {
            this.municipios = municipios
              .map((municipio: any) => ({
                label: municipio.nome,
                value: municipio.id
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
            this.spinnerService.hide();
          });
        }
      }
    });

    this.dadosForm.get('municipio')?.valueChanges.subscribe(municipioId => {
      if (municipioId && this.municipios.length > 0) {
        const municipio = this.municipios.find(m => m.value === municipioId);
        if (municipio) {
          // Habilitar temporariamente para setar o valor, depois desabilitar novamente
          this.dadosForm.get('codigoMunicipio')?.enable({ emitEvent: false });
          this.dadosForm.get('codigoMunicipio')?.setValue(municipio.value.toString(), { emitEvent: false });
          this.dadosForm.get('codigoMunicipio')?.disable({ emitEvent: false });
        }
      }
    });
  }

  carregarDetalhesEmpresa(): void {
    this.spinnerService.show('Carregando informações da empresa...');
    
    this.authService.obterDetalhesEmpresa(this.userId).subscribe({
      next: (res) => {
        this.empresa = res;
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Informações da empresa carregadas com sucesso!', SeveridadeToast.Success);
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Erro ao carregar detalhes da empresa:', error);
        this.toastService.setMensagemToast(
          'Erro ao carregar informações da empresa. Tente novamente.',
          SeveridadeToast.Error
        );
      }
    });
  }

  formatarCpfCnpj(cpfCnpj: string): string {
    if (!cpfCnpj) return '';
    return FormatadorHelper.formatarCpfCnpj(cpfCnpj);
  }

  formatarTelefone(telefone: string): string {
    if (!telefone) return '';
    return FormatadorHelper.formatarTelefone(telefone);
  }

  formatarCep(cep: string): string {
    if (!cep) return '';
    return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
  }

  applyFilterGlobal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filterValue = input.value;
    if (this.dt) {
      this.dt.filterGlobal(filterValue, 'contains');
    }
  }

  onFileChange(event: any): void {
    const files = event?.files as File[] | undefined;
    const file = files && files.length > 0 ? files[0] : null;

    if (!file) {
      this.certificadoBaseList = [];
      this.fileName = null;
      return;
    }

    const erro = validarArquivoCertificado(file);
    if (erro) {
      this.certificadoBaseList = [];
      this.fileName = null;
      this.certificadoInvalid = true;
      this.toastService.setMensagemToast(erro, SeveridadeToast.Error);
      return;
    }

    this.certificadoBaseList = [file];
    this.fileName = file.name;
    this.certificadoInvalid = false;
  }

  atualizarCertificado(): void {
    if (!this.certificadoBaseList || this.certificadoBaseList.length === 0) {
      this.certificadoInvalid = true;
      this.toastService.setMensagemToast('Por favor, selecione um arquivo de certificado (.pfx ou .p12).', SeveridadeToast.Error);
      return;
    }

    const erroFormato = validarArquivoCertificado(this.certificadoBaseList[0]);
    if (erroFormato) {
      this.certificadoInvalid = true;
      this.toastService.setMensagemToast(erroFormato, SeveridadeToast.Error);
      return;
    }

    if (!this.senhaCertificado || this.senhaCertificado.trim() === '') {
      this.certificadoInvalid = true;
      this.toastService.setMensagemToast('Por favor, informe a senha do certificado.', SeveridadeToast.Error);
      return;
    }

    this.isSubmitting = true;
    this.certificadoInvalid = false;

    const formData = new FormData();
    formData.append('ArquivoCertificadoBase64', this.certificadoBaseList[0]);
    formData.append('SenhaCertificado', this.senhaCertificado);

    this.spinnerService.show('Atualizando certificado...');
    this.authService.atualizarCertificado(this.userId, formData).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Certificado atualizado com sucesso!', SeveridadeToast.Success);
        this.certificadoBaseList = [];
        this.fileName = null;
        this.senhaCertificado = '';
        this.isSubmitting = false;
      },
      error: (error) => {
        this.spinnerService.hide();
        this.isSubmitting = false;
        console.error('Erro ao atualizar certificado:', error);
        const errorMessage = error.error?.message || error.message || 'Erro ao atualizar certificado. Tente novamente.';
        this.toastService.setMensagemToast(errorMessage, SeveridadeToast.Error);
      }
    });
  }

  entrarModoEdicao(): void {
    if (!this.empresa) return;

    this.isEditMode = true;
    const regimeApuracaoValue = this.empresa.regimeApuracaoSimplesNacional === 'Caixa' 
      ? RegimeApuracaoSimplesNacional.Caixa 
      : RegimeApuracaoSimplesNacional.Competencia;

    // Habilitar temporariamente para setar o valor do codigoMunicipio
    this.dadosForm.get('codigoMunicipio')?.enable({ emitEvent: false });
    
    this.dadosForm.patchValue({
      email: this.empresa.email || '',
      telefone: this.empresa.telefone || '',
      regimeApuracaoSimplesNacional: regimeApuracaoValue,
      logradouro: this.empresa.logradouro || '',
      numero: this.empresa.numero || '',
      complemento: this.empresa.complemento || '-',
      bairro: this.empresa.bairro || '',
      uf: this.empresa.uf || '',
      cep: this.empresa.cep || '',
      codigoMunicipio: this.empresa.codigoMunicipio ? this.empresa.codigoMunicipio.toString() : '',
      codigoTributarioMunicipio: this.empresa.codigoTributarioMunicipio || '',
      pais: this.empresa.pais || 'BR'
    });
    
    // Desabilitar novamente após o patchValue
    this.dadosForm.get('codigoMunicipio')?.disable({ emitEvent: false });

    if (this.empresa.uf) {
      const estado = this.estados.find(e => e.value === this.empresa?.uf);
      if (estado) {
        this.spinnerService.show('Carregando municípios...');
        this.locationService.getMunicipiosPorUf(estado.id).subscribe(municipios => {
          this.municipios = municipios
            .map((municipio: any) => ({
              label: municipio.nome,
              value: municipio.id
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          
          // Buscar município pelo código do município cadastrado
          if (this.empresa?.codigoMunicipio) {
            const municipioEncontrado = this.municipios.find(m => 
              m.value.toString() === this.empresa?.codigoMunicipio?.toString()
            );
            if (municipioEncontrado) {
              // Habilitar temporariamente para setar os valores
              this.dadosForm.get('codigoMunicipio')?.enable({ emitEvent: false });
              this.dadosForm.get('municipio')?.setValue(municipioEncontrado.value);
              this.dadosForm.get('codigoMunicipio')?.setValue(this.empresa.codigoMunicipio.toString(), { emitEvent: false });
              this.dadosForm.get('codigoMunicipio')?.disable({ emitEvent: false });
            }
          }
          this.spinnerService.hide();
        });
      }
    }
  }

  cancelarEdicao(): void {
    this.isEditMode = false;
    this.dadosForm.reset();
  }

  atualizarDados(): void {
    if (this.dadosForm.invalid) {
      this.dadosForm.markAllAsTouched();
      this.toastService.setMensagemToast('Por favor, preencha todos os campos obrigatórios.', SeveridadeToast.Error);
      return;
    }

    this.isSubmittingDados = true;
    // Usar getRawValue() para incluir campos disabled
    const formValue = this.dadosForm.getRawValue();
    const municipioSelecionado = this.municipios.find(m => m.value === formValue.municipio);

    // Garantir que codigoMunicipio seja uma string válida
    let codigoMunicipioValue = '';
    if (formValue.codigoMunicipio !== null && formValue.codigoMunicipio !== undefined) {
      codigoMunicipioValue = String(formValue.codigoMunicipio);
    } else if (municipioSelecionado) {
      codigoMunicipioValue = String(municipioSelecionado.value);
    } else if (this.empresa?.codigoMunicipio) {
      codigoMunicipioValue = String(this.empresa.codigoMunicipio);
    }

    // Garantir que todos os valores sejam strings válidas
    const dados = {
      email: String(formValue.email || ''),
      telefone: String(formValue.telefone || ''),
      regimeApuracaoSimplesNacional: formValue.regimeApuracaoSimplesNacional ?? null,
      logradouro: String(formValue.logradouro || ''),
      numero: String(formValue.numero || ''),
      complemento: String(formValue.complemento || '-'),
      bairro: String(formValue.bairro || ''),
      uf: String(formValue.uf || ''),
      municipio: String(municipioSelecionado ? municipioSelecionado.label : (formValue.municipio || '')),
      cep: String(formValue.cep || ''),
      codigoMunicipio: codigoMunicipioValue,
      codigoTributarioMunicipio: String(formValue.codigoTributarioMunicipio || ''),
      pais: String(formValue.pais || 'BR')
    };

    console.log('Dados a serem enviados:', dados);

    this.spinnerService.show('Atualizando dados...');
    this.authService.atualizarDadosUsuario(this.userId, dados).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Dados atualizados com sucesso!', SeveridadeToast.Success);
        this.isEditMode = false;
        this.isSubmittingDados = false;
        this.carregarDetalhesEmpresa();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.isSubmittingDados = false;
        console.error('Erro ao atualizar dados:', error);
        const errorMessage = error.error?.message || error.message || 'Erro ao atualizar dados. Tente novamente.';
        this.toastService.setMensagemToast(errorMessage, SeveridadeToast.Error);
      }
    });
  }

  get f() {
    return this.dadosForm.controls;
  }
}
